import { useMemo, useState } from "react";
import { ChoiceField, NumField } from "@/components/tool/CalcFields";
import { numField } from "@/lib/health/body-profile";
import { type IdealFormula, idealWeight, type Sex } from "@/lib/health/fitness";
import {
  BodyProfileHint,
  useBodyProfileSync,
} from "@/lib/health/use-body-profile";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

const FORMULAS: IdealFormula[] = ["devine", "robinson", "miller", "hamwi"];

export function IdealWeightCalc() {
  const locale = getLocale();
  const [sex, setSex] = useState<Sex>("male");
  const [height, setHeight] = useState("180");

  const profileSync = useBodyProfileSync({
    apply: (p) => {
      if (p.sex) setSex(p.sex);
      if (p.heightCm) setHeight(String(p.heightCm));
    },
    collect: () => ({ sex, heightCm: numField(height) }),
    deps: [sex, height],
  });

  const out = useMemo(() => {
    const h = Number(height);
    if (!Number.isFinite(h) || h <= 0) return null;
    return FORMULAS.map((f) => ({ f, kg: idealWeight(sex, h, f) }));
  }, [sex, height]);

  const kg = (n: number) =>
    `${n.toLocaleString(locale, { maximumFractionDigits: 1 })} kg`;

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <ChoiceField
        label={m.h_sex()}
        value={sex}
        onChange={setSex}
        options={[
          { value: "male", label: m.h_male() },
          { value: "female", label: m.h_female() },
        ]}
      />
      <NumField
        id="height"
        label={m.h_height_cm()}
        value={height}
        onChange={setHeight}
        suffix="cm"
      />
      {out ? (
        <>
          <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.iw_label()}
            </span>
            <span
              data-testid="iw-result"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {kg(out[0].kg)}
            </span>
          </div>
          <SciStatGrid
            items={out.map((o) => ({
              label: m[`iw_${o.f}` as "iw_devine"](),
              value: kg(o.kg),
            }))}
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{m.h_hint()}</p>
      )}
      <BodyProfileHint {...profileSync} />
    </div>
  );
}
